import { validate as uuidvalidate } from 'uuid';

import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import { log } from '~/utils/logging';

import {
  CUSTOM_FIELD_LEVEL,
  CUSTOM_FIELD_TYPE,
  CUSTOM_FIELD_VISIBILITY,
  DIFFERENCE_VALUES,
} from './constants';
import { CustomFieldOptionObject } from './customFieldOptionUtils';
import { type CustomField } from './types';

export const CustomFieldObject = {
  /**
   * Creates a new custom field object with provided options.
   * @param customField - A partial representation of a custom field, containing key-value pairs like `id`, `name`, etc.
   * @param customFieldOptions - An array of options for the custom field, each formatted as a key-value object.
   * @returns A fully-formed custom field object with all default values and transformed options.
   */
  create(
    customField: Partial<CustomField> = {},
    customFieldOptions: any[] = [],
  ) {
    return {
      allowedUnits: customField.allowedUnits ?? [],
      defaultUnit: customField.defaultUnit ?? undefined,
      displayName: customField.displayName ?? undefined,
      hardcoded: customField.hardcoded ?? false,
      hasOptions: customField.hasOptions ?? false,
      helpText: customField.helpText ?? undefined,
      id: customField.id ?? undefined,
      key: customField.id?.slice(0, 36) ?? undefined, // Old customField keys might consist of UUID + label; only get the UUID
      level: customField.level ?? CustomFieldObject.LEVEL.DELIVERY_NOTE.KEY,
      name: customField.name ?? undefined,
      options: customFieldOptions.map((option) =>
        CustomFieldOptionObject.create(option),
      ),
      public: customField.public ?? false,
      type: customField.type ?? CustomFieldObject.TYPE.STRING.KEY,
      usedBy: customField.usedBy ?? [],
      visibility:
        customField.visibility ?? CustomFieldObject.VISIBILITY.ALWAYS.KEY,
    };
  },

  /**
   * Retrieves all available custom field levels.
   * @returns An array of objects, each containing `id` and `name` for a custom field level.
   */
  getCustomFieldLevels() {
    return Object.keys(this.LEVEL).map((key) => ({
      id: this.LEVEL[key].KEY,
      name: this.LEVEL[key].STRING,
    }));
  },

  /**
   * Retrieves all available custom field types.
   * @returns An array of objects, each containing `id`, `name`, and `disabled` status for a custom field type.
   */
  getCustomFieldTypes() {
    return Object.keys(this.TYPE).map((key) => ({
      disabled: this.TYPE[key].DISABLED,
      id: this.TYPE[key].KEY,
      name: this.TYPE[key].STRING,
    }));
  },

  /**
   * Retrieves all available visibility options.
   * @returns An array of objects, each containing `id` and `name` for a visibility option.
   */
  getCustomFieldVisibilities() {
    return Object.keys(this.VISIBILITY).map((key) => ({
      id: this.VISIBILITY[key].KEY,
      name: this.VISIBILITY[key].STRING,
    }));
  },

  /**
   * Compares two custom fields and returns their differences.
   * @param customFieldA - The first custom field object to compare.
   * @param customFieldB - The second custom field object to compare.
   * @returns An array of strings representing the keys of differing properties between the two fields.
   */
  getDifferences(
    customFieldA: Record<string, any>,
    customFieldB: Record<string, any>,
  ) {
    const differentValues: string[] = [];
    if (customFieldA.id !== customFieldB.id) {
      differentValues.push(DIFFERENCE_VALUES.ID);
    }

    if (customFieldA.name !== customFieldB.name) {
      differentValues.push(DIFFERENCE_VALUES.NAME);
    }

    if (customFieldA.key !== customFieldB.key) {
      differentValues.push(DIFFERENCE_VALUES.KEY);
    }

    if (customFieldA.displayName !== customFieldB.displayName) {
      differentValues.push(DIFFERENCE_VALUES.DISPLAY_NAME);
    }

    if (customFieldA.type !== customFieldB.type) {
      differentValues.push(DIFFERENCE_VALUES.TYPE);
    }

    if (customFieldA.hasOptions !== customFieldB.hasOptions) {
      differentValues.push(DIFFERENCE_VALUES.HAS_OPTIONS);
    }

    if (customFieldA.level !== customFieldB.level) {
      differentValues.push(DIFFERENCE_VALUES.LEVEL);
    }

    if (customFieldA.public !== customFieldB.public) {
      differentValues.push(DIFFERENCE_VALUES.PUBLIC);
    }

    if (customFieldA.visibility !== customFieldB.visibility) {
      differentValues.push(DIFFERENCE_VALUES.VISIBILITY);
    }

    if (
      JSON.stringify(customFieldA.options) !==
        JSON.stringify(customFieldB.options) &&
      customFieldA.options &&
      customFieldB.options
    ) {
      differentValues.push(DIFFERENCE_VALUES.OPTIONS);
    }

    if (
      JSON.stringify(customFieldA.allowedUnits) !==
      JSON.stringify(customFieldB.allowedUnits)
    ) {
      differentValues.push(DIFFERENCE_VALUES.ALLOWED_UNITS);
    }

    if (
      JSON.stringify(customFieldA.defaultUnit) !==
      JSON.stringify(customFieldB.defaultUnit)
    ) {
      differentValues.push(DIFFERENCE_VALUES.DEFAULT_UNIT);
    }

    if (
      JSON.stringify(customFieldA.usedBy) !==
      JSON.stringify(customFieldB.usedBy)
    ) {
      differentValues.push(DIFFERENCE_VALUES.USED_BY);
    }

    return differentValues;
  },

  /**
   * Gets the formatter function for a specific custom field type.
   * @param key - The unique key of a custom field type.
   * @returns A function that formats values for the specified type, or `null` if the key is invalid.
   */
  getFormatter(key: string) {
    const type = Object.keys(this.TYPE).find((x) => this.TYPE[x].KEY === key);
    if (!type) {
      log.error({
        message: `Invalid custom field type: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid custom field type: ${key}`,
        ),
      });
      return null;
    }

    return this.TYPE[type].FORMATTER;
  },

  /**
   * Formats a custom field value (or array of values, for multi-value
   * article-level fields) for display in a data grid cell, delegating to
   * {@link getOptionDisplayValue} for the actual value-to-label lookup.
   *
   * @param value - The raw value, or an array of raw values
   * @param customField - The custom field definition with options
   * @returns A display-friendly string, joining array values with `, `
   */
  getGridDisplayValue(
    value: unknown,
    customField: Record<string, any> | undefined,
  ): string {
    if ([undefined, null, ''].includes(value as any)) {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .map((entry) => this.getOptionDisplayValue(entry, customField))
        .filter((entry) => ![undefined, null, ''].includes(entry))
        .join(', ');
    }

    return this.getOptionDisplayValue(value, customField);
  },

  /**
   * Extracts the ID from a custom field key.
   * @param key - A string containing the unique key of a custom field, typically including an ID as a prefix.
   * @returns The UUID extracted from the key if valid, otherwise `null`.
   */
  getIdFromKey(key: string) {
    if (typeof key !== 'string') {
      return null;
    }

    const id = key.slice(0, 36);
    return uuidvalidate(id) ? id : null;
  },

  /**
   * Gets the string representation of a custom field level by its key.
   * @param key - The unique key of a custom field level.
   * @returns The string representation of the level if the key is valid, otherwise `null`.
   */
  getLevelString(key: string) {
    const level = Object.keys(this.LEVEL).find(
      (x) => this.LEVEL[x].KEY === key,
    );
    if (!level) {
      log.error({
        message: `Invalid custom field level: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid custom field level: ${key}`,
        ),
      });
      return null;
    }

    return this.LEVEL[level].STRING;
  },

  /**
   * Transforms a custom field value (which may be a UUID) to its display name.
   * For ENUMERATION type fields, looks up the option by ID and returns displayName.
   * For other types, applies the appropriate formatter.
   *
   * @param value - The raw value (may be a UUID for enumeration fields)
   * @param customField - The custom field definition with options
   * @returns The display-friendly value
   */
  getOptionDisplayValue(
    value: any,
    customField: Record<string, any> | undefined,
  ) {
    if (value === null || value === undefined) {
      return '';
    }

    if (!customField) {
      return value;
    }

    // For enumeration types with options, look up the display name
    if (
      customField.type === this.TYPE.ENUMERATION.KEY &&
      customField.hasOptions &&
      customField.options?.length > 0
    ) {
      const valueId = this.getIdFromKey(String(value));
      if (valueId) {
        const option = customField.options.find(
          (opt: { id: string; displayName: string }) => opt.id === valueId,
        );
        if (option?.displayName) {
          return option.displayName;
        }
      }
    }

    // For other types, apply the formatter

    const formatter = this.getFormatter(customField.type);

    if (formatter) {
      return formatter(value);
    }

    return value;
  },

  /**
   * Gets the string representation of a custom field type by its key.
   * @param key - The unique key of a custom field type.
   * @returns The string representation of the type if the key is valid, otherwise `null`.
   */
  getTypeString(key: string) {
    const type = Object.keys(this.TYPE).find((x) => this.TYPE[x].KEY === key);
    if (!type) {
      log.error({
        message: `Invalid custom field type: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid custom field type: ${key}`,
        ),
      });
      return null;
    }

    return this.TYPE[type].STRING;
  },

  /**
   * Gets the string representation of a custom field visibility by its key.
   * @param key - The unique key of a custom field visibility.
   * @returns The string representation of the visibility if the key is valid, otherwise `null`.
   */
  getVisibilityString(key: string) {
    const visibility = Object.keys(this.VISIBILITY).find(
      (x) => this.VISIBILITY[x].KEY === key,
    );
    if (!visibility) {
      log.error({
        message: `Invalid custom field visibility: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid custom field visibility: ${key}`,
        ),
      });
      return null;
    }

    return this.VISIBILITY[visibility].STRING;
  },

  LEVEL: CUSTOM_FIELD_LEVEL,

  TYPE: CUSTOM_FIELD_TYPE,

  /**
   * Checks if a custom field value is empty.
   * @param value - The value to check, which could be of any type.
   * @returns `true` if the value is empty (`null` or an empty string), otherwise `false`.
   */
  valueIsEmpty(value: any) {
    return value === '' || value === null;
  },

  VISIBILITY: CUSTOM_FIELD_VISIBILITY,
};
